import {Component, EventEmitter, Input, Output} from '@angular/core';
import {CommonModule} from '@angular/common';
import {GraphMap, GraphNodeLink} from '../../api/interfaces/graph-node.interface';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatDividerModule} from '@angular/material/divider';
import {MetaDataViewerComponent} from '../../shared/forms/meta-data-viewer/meta-data-viewer.componet';
import {
  TitleDescriptionInputComponent
} from '../../shared/forms/title-description-input/title-description-input.component';
import {CdkAccordion} from '@angular/cdk/accordion';
import {
  MaporiumAccordionItemComponent
} from '../../shared/components/maporium-accordion/maporium-accordion-item.component';
import {CommentsComponent} from '../../shared/components/comments/comments.component';
import {isInIframe} from '../../shared/helpers/maporium.validators';
import {HasMapPermissionDirective} from '../../packages/has-map-permission.directive';
import {MapService} from '../../api/services/map.service';
import {PackageService} from '../../api/services/package.service';
import {TourMatMenuModule} from 'ngx-ui-tour-md-menu';
import {BottleneckMarkerComponent} from '../bottleneck-marker/bottleneck-marker.component';
import {visibleMetadata} from '../../shared/helpers/system-metadata.helpers';
import {HasPackageDirective} from '../../packages/has-package.directive';
import {PACKAGE_NAMES} from '@maporium-workspace/shared';
import {TranslatePipe} from '@ngx-translate/core';

@Component({
  selector: 'maporium-link-view',
  standalone: true,
  template: `
    <ng-container *ngIf="focusedLink">
      <maporium-title-description [isEditing]="false"
                                  [entityId]="focusedLink.id"
                                  [fields]="{title: focusedLink.name || '', description: focusedLink.description}">
      </maporium-title-description>

      @if (focusedLink.metadata?.length && isInIframe()) {
        <mat-divider class="mb-2 mt-2"></mat-divider>
      }

      <div class="mt-1">
        @if (!isInIframe()) {
          <cdk-accordion [multi]="true" class="accordion">
            <maporium-accordion-item [title]="'MAP_INFO.PROPERTIES' | translate"
                                     [iconClass]="'icon-smap-object-property'"
                                     tourAnchor="properties-anchor"
                                     [sections]="visibleMetaCount">
              <ng-container *appHasPackage="[PACKAGE_NAMES.developer]">
                <maporium-bottleneck-marker [metadata]="focusedLink.metadata"
                                            [readonly]="true"></maporium-bottleneck-marker>
              </ng-container>
              <maporium-meta-viewer [metadata]="focusedLink.metadata"></maporium-meta-viewer>
            </maporium-accordion-item>
            <maporium-accordion-item [title]="'MAP_INFO.COMMENTS' | translate"
                                     [iconClass]="'icon-smap-object-comment'"
                                     [sections]="commentsCount" *hasMapPermission="'comment'">
              <maporium-comments [entityId]="focusedLink!.id"
                                 (updated)="hasComments.emit($event)"
                                 (commentCount)="commentsCount = $event"
                                 [entityType]="'link'"
                                 [isEditing]="!isDefaulter"></maporium-comments>
            </maporium-accordion-item>
          </cdk-accordion>
        } @else {
          <ng-container *appHasPackage="[PACKAGE_NAMES.developer]">
            <maporium-bottleneck-marker [metadata]="focusedLink.metadata"
                                        [readonly]="true"></maporium-bottleneck-marker>
          </ng-container>
          <maporium-meta-viewer [metadata]="focusedLink.metadata"></maporium-meta-viewer>
          @if (currentMap.hasGuestComments) {
            <maporium-accordion-item [title]="'MAP_INFO.COMMENTS' | translate"
                                     [iconClass]="'icon-smap-object-comment'"
                                     [sections]="commentsCount">
              <maporium-comments [entityId]="focusedLink!.id"
                                 class="mt-2"
                                 (commentCount)="commentsCount = $event"
                                 [entityType]="'link'"
                                 [isEditing]="!isDefaulter"></maporium-comments>
            </maporium-accordion-item>
          }
        }
      </div>
    </ng-container>
  `,
  imports: [
    CommonModule,
    MatTooltipModule,
    MatDividerModule,
    MetaDataViewerComponent,
    TitleDescriptionInputComponent,
    CdkAccordion,
    MaporiumAccordionItemComponent,
    CommentsComponent,
    HasMapPermissionDirective,
    TourMatMenuModule,
    BottleneckMarkerComponent,
    HasPackageDirective,
    TranslatePipe
  ]
})
export class LinkViewComponent {
  protected readonly PACKAGE_NAMES = PACKAGE_NAMES;
  @Input() focusedLink: GraphNodeLink | undefined;
  @Output() hasComments = new EventEmitter<boolean>();
  commentsCount = 0;
  currentMap: GraphMap;

  get isDefaulter(): boolean {
    return this.packageService.isDefaulter();
  }

  constructor(private mapService: MapService, private packageService: PackageService) {
    this.currentMap = this.mapService.getCurrentSelectedMapFromStore();
  }

  get metadata(): any[]{
    return this.focusedLink?.metadata || [];
  }

  get visibleMetaCount(): number {
    return visibleMetadata(this.focusedLink?.metadata).length;
  }

  protected readonly isInIframe = isInIframe;
}
